import { Link } from '@tanstack/react-router'
import { ArrowLeft, ExternalLink } from 'lucide-react'
import type { ReactNode } from 'react'
import styles from './cms-shell.module.css'
import { CmsEditorTabs, type CmsEditorTab } from './CmsEditorTabs'
import { PageLayout } from '@/components/layout/PageLayout'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'

/**
 * Chrome shared by every CMS editor tab: title, lifecycle badge, tab strip, and
 * the card the tab body sits in.
 *
 * Presentational only — each surface's own shell (`PlaybookEditorShell`,
 * `MasterclassEditorShell`) loads its record and renders this. That split is
 * what lets the two share the chrome without sharing a query.
 *
 * A render prop rather than a router layout route, because the admin routes in
 * router.tsx are flat siblings — nesting them would restructure a working
 * router for cosmetic reasons and put the pre-existing playbook `/schema` URLs
 * at risk.
 *
 * An ordinary scrolling page, like every other admin detail screen: editor
 * routes deliberately do NOT set `staticData: { fullHeight: true }`, so
 * DashboardLayout keeps them inside the dashboard ScrollArea. A page only opts
 * out of that when it hands its own scroll to a child (the playbook Brief tab
 * does, via SchemaEditor); these are forms of arbitrary length with nothing to
 * hand it to.
 */
export function CmsEditorShell({
  title,
  badge,
  subtitle,
  tabs,
  tabsLabel,
  params,
  actions,
  children,
}: {
  title: ReactNode
  /** Lifecycle badge rendered beside the title. */
  badge?: ReactNode
  subtitle: string
  tabs: readonly CmsEditorTab[]
  tabsLabel: string
  params: Record<string, string>
  actions?: ReactNode
  children: ReactNode
}) {
  return (
    <PageLayout
      title={
        <span className={styles.title}>
          {title}
          {badge}
        </span>
      }
      subtitle={subtitle}
      actions={actions ? <div className={styles.headerActions}>{actions}</div> : undefined}
    >
      <Card className={styles.editorCard}>
        <CmsEditorTabs tabs={tabs} params={params} label={tabsLabel} />
        {children}
      </Card>
    </PageLayout>
  )
}

/**
 * The two buttons every CMS editor header carries: "View live" and the way back
 * to the list.
 *
 * "View live" is rendered only for a published record — the founder route 404s
 * a draft, which would be a confusing thing to offer. That rule lives here so
 * the surfaces cannot answer it differently.
 */
export function CmsEditorHeaderActions({
  live,
  backTo,
  backLabel,
}: {
  /** The founder-facing route, or null when the record is not published. */
  live: { to: string; params: Record<string, string> } | null
  backTo: string
  backLabel: string
}) {
  return (
    <>
      {live ? (
        <Button variant="outline" asChild>
          <Link to={live.to} params={live.params} preload="intent">
            <ExternalLink className="w-4 h-4 mr-2" aria-hidden />
            View live
          </Link>
        </Button>
      ) : null}
      <Button variant="outline" asChild>
        <Link to={backTo} preload="intent">
          <ArrowLeft className="w-4 h-4 mr-2" aria-hidden />
          {backLabel}
        </Link>
      </Button>
    </>
  )
}

/** "That record does not exist", with the way back. */
export function CmsEditorNotFound({
  title,
  message,
  backTo,
  backLabel,
}: {
  title: string
  message: string
  backTo: string
  backLabel: string
}) {
  return (
    <PageLayout title={title} subtitle="Not found">
      <Card className={styles.notFound}>
        <p>{message}</p>
        <Button variant="outline" asChild>
          <Link to={backTo} preload="intent">
            <ArrowLeft className="w-4 h-4 mr-2" aria-hidden />
            {backLabel}
          </Link>
        </Button>
      </Card>
    </PageLayout>
  )
}

/** The editor's loading state — a tab strip and a form, in outline. */
export function CmsEditorSkeleton({ tabCount = 6 }: { tabCount?: number }) {
  return (
    <PageLayout title={<Skeleton className="h-7 w-56" />} subtitle="Loading…">
      <Card className={styles.editorCard}>
        <div className={styles.tabs}>
          {[64, 96, 80, 56, 48, 64].slice(0, tabCount).map((w, i) => (
            <Skeleton key={i} className="h-8" style={{ width: w }} />
          ))}
        </div>
        <div className={styles.form}>
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      </Card>
    </PageLayout>
  )
}
